<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid
      :desktop-default-grid="[2, 20]"
      :tablet-default-grid="[1, 14]"
      :mobile-default-grid="[1, 14]"
    >
      <div class="c-workflow-cmc__inner">
        <JssRichText class="c-workflow-cmc__title" :field="fields?.title" tag="h2" />

        <div v-if="steps.length" class="c-workflow-cmc__workflow" :style="workflowStyle">
          <span class="c-workflow-cmc__timeline-line" aria-hidden="true"></span>

          <article
            v-for="step in steps"
            :key="step.id || step.fields.title?.value"
            class="c-workflow-cmc__step"
          >
            <h3 v-if="step.fields.title?.value" class="c-workflow-cmc__step-title">
              {{ step.fields.title.value }}
            </h3>
            <span class="c-workflow-cmc__icon-ring">
              <img
                v-if="getIconSrc(step)"
                class="c-workflow-cmc__icon"
                :src="getIconSrc(step)"
                :alt="getIconAlt(step)"
                loading="lazy"
              />
            </span>
            <span v-if="step.fields.milestoneLabel?.value" class="c-workflow-cmc__milestone">
              {{ step.fields.milestoneLabel.value }}
            </span>
          </article>
        </div>

        <div v-if="summaryCards.length" class="c-workflow-cmc__summary">
          <article
            v-for="card in summaryCards"
            :key="card.id || card.fields.text?.value"
            class="c-workflow-cmc__summary-card"
            :style="getSummaryCardStyle(card)"
          >
            {{ card.fields.text.value }}
          </article>
        </div>

        <div v-if="fields.ctaText?.value || ctaHref" class="c-workflow-cmc__cta">
          <p v-if="fields.ctaText?.value" class="c-workflow-cmc__cta-text">
            {{ fields.ctaText.value }}
          </p>
          <a
            v-if="ctaHref"
            class="c-workflow-cmc__cta-link"
            :href="ctaHref"
            :target="ctaTarget"
            :title="ctaTitle"
          >
            {{ ctaLabel }}
          </a>
        </div>

        <RichText
          v-if="fields.footnote?.value"
          class="c-workflow-cmc__footnote"
          :field="fields.footnote"
        />
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText } from '@sitecore-jss/sitecore-jss-vue';
import type {
  WorkflowCMCProps,
  WorkflowCMCSummaryCardFields,
  WorkflowCMCStepFields,
} from '@/types/components/WorkflowCMC';
import type { ScItem } from '@/types/sitecore';
import utils from '@/utils';

type WorkflowCMCStep = ScItem<WorkflowCMCStepFields> & { fields: WorkflowCMCStepFields };
type WorkflowCMCSummaryCard = ScItem<WorkflowCMCSummaryCardFields> & {
  fields: WorkflowCMCSummaryCardFields;
};

const props = defineProps<WorkflowCMCProps>();
const fields = computed(() => props.fields);

const rootClass = computed(() => utils.overall.baseComClass(props.fields, 'c-workflow-cmc'));
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});

const steps = computed<WorkflowCMCStep[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'steps');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowCMCStep => Boolean(item?.fields?.title?.value))
    : [];
});

const summaryCards = computed<WorkflowCMCSummaryCard[]>(() => {
  const items = utils.sc.getFieldObj(fields.value, 'summaryCards');
  return Array.isArray(items)
    ? items.filter((item): item is WorkflowCMCSummaryCard => Boolean(item?.fields?.text?.value))
    : [];
});

const workflowStyle = computed(() => ({
  '--workflow-step-count': `${Math.max(steps.value.length, 1)}`,
}));

const ctaHref = computed(() => fields.value?.ctaLink?.value?.href || '');
const ctaTarget = computed(() => fields.value?.ctaLink?.value?.target || undefined);
const ctaTitle = computed(() => fields.value?.ctaLink?.value?.title || undefined);
const ctaLabel = computed(() => fields.value?.ctaLink?.value?.text || 'Talk to an Expert');

const getIconValue = (step: WorkflowCMCStep) => {
  const icon = step.fields.icon?.value as
    | {
        src?: string;
        alt?: string;
        svgCode?: string;
        value?: {
          src?: string;
          alt?: string;
          svgCode?: string;
        };
      }
    | undefined;

  return icon?.value || icon || {};
};

const getSvgDataUri = (svgCode?: string) => {
  if (!svgCode) {
    return '';
  }

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgCode)}`;
};

const getIconSrc = (step: WorkflowCMCStep) => {
  const icon = getIconValue(step);
  return icon.src || getSvgDataUri(icon.svgCode);
};
const getIconAlt = (step: WorkflowCMCStep) =>
  getIconValue(step).alt || step.fields.title?.value || '';

const getSummaryCardStyle = (card: WorkflowCMCSummaryCard) => {
  const gridStart = Number(card.fields.gridStart?.value ?? 0);
  const gridSpan = Number(card.fields.gridSpan?.value ?? 0);

  if (!Number.isFinite(gridStart) || gridStart <= 0) {
    return {};
  }

  return {
    '--summary-grid-start': `${gridStart}`,
    '--summary-grid-span': `${Number.isFinite(gridSpan) && gridSpan > 0 ? gridSpan : 2}`,
  };
};
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-workflow-cmc {
  padding: 38px 0 44px;
  background: #fff;
  color: #3d3d3f;

  &__inner {
    width: 100%;
  }

  &__title {
    margin: 0;
    color: #3d3d3f;
    font-size: 23px;
    line-height: 1.18;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0;
  }

  &__workflow {
    position: relative;
    display: grid;
    grid-template-columns: 1fr;
    gap: 18px;
    margin-top: 30px;
  }

  &__timeline-line {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 23px;
    z-index: 0;
    width: 1px;
    background: linear-gradient(90deg, #d4d4d4, #a8a8a8);
  }

  &__step {
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: 74px 1fr;
    gap: 13px;
    align-items: center;
    min-height: 84px;
  }

  &__step-title {
    grid-column: 2;
    grid-row: 1;
    margin: 0;
    color: #3d3d3f;
    font-size: 14px;
    line-height: 1.22;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__icon-ring {
    grid-column: 1;
    grid-row: 1 / span 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #fff;
    position: relative;
    z-index: 3;
  }

  &__icon {
    width: 44px;
    height: 44px;
    object-fit: contain;
  }

  &__milestone {
    grid-column: 2;
    grid-row: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: 88px;
    min-height: 30px;
    padding: 6px 14px;
    border-radius: 999px;
    background: #f0f0f0;
    color: #3d3d3f;
    font-size: 14px;
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: 0;
  }

  &__summary {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    margin-top: 24px;
  }

  &__summary-card {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 10px;
    border: 1px solid #b7cbbd;
    border-radius: 3px;
    background: #fff;
    color: #3d3d3f;
    font-size: 14px;
    line-height: 1.28;
    font-weight: 400;
    text-align: center;
    letter-spacing: 0;
  }

  &__cta {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
    align-items: center;
    margin-top: 22px;
    padding: 10px 12px;
    background: #f7faf8;
  }

  &__cta-text {
    margin: 0;
    color: #3d3d3f;
    font-size: 16px;
    line-height: 1.24;
    font-weight: 400;
    letter-spacing: 0;
  }

  &__cta-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    min-width: 150px;
    min-height: 38px;
    padding: 10px 18px;
    border-radius: 3px;
    background: #6f9877;
    color: #fff;
    font-size: 14px;
    line-height: 1.1;
    font-weight: 500;
    text-decoration: none;
    letter-spacing: 0;
    transition: background-color 0.2s ease;
  }

  &__cta-link:hover,
  &__cta-link:focus-visible {
    background: #5d8465;
    color: #fff;
  }

  &__footnote {
    margin-top: 18px;
    color: #4c4c4c;
    font-size: 12px;
    line-height: 1.45;
    letter-spacing: 0;
    text-align: left;
  }

  &__footnote p {
    text-align: center;
    margin: 0;
  }

  @include tablet {
    padding: 44px 0 48px;

    &__title {
      font-size: 28px;
    }

    &__workflow {
      grid-template-columns: repeat(var(--workflow-step-count), minmax(0, 1fr));
      gap: 0;
      margin-top: 32px;
    }

    &__timeline-line {
      top: 79px;
      bottom: auto;
      left: 0;
      right: 0;
      width: auto;
      height: 2px;
      background: #e1e6e3;
    }

    &__step {
      display: flex;
      flex-direction: column;
      gap: 12px;
      align-items: center;
      min-height: 176px;
      padding: 0 6px;
    }

    &__step-title {
      display: flex;
      align-items: flex-end;
      justify-content: center;
      min-height: 42px;
      font-size: 13px;
      line-height: 1.2;
      text-align: center;
    }

    &__icon-ring {
      width: 45px;
      height: 45px;
      border-width: 1.5px;
    }

    &__icon {
      width: 45px;
      height: 45px;
    }

    &__milestone {
      min-width: 92px;
      min-height: 32px;
      padding: 7px 16px;
      font-size: 14px;
    }

    &__summary {
      grid-template-columns: repeat(var(--workflow-step-count), minmax(0, 1fr));
      gap: 14px;
      margin-top: 22px;
    }

    &__summary-card {
      grid-column: var(--summary-grid-start, auto) / span var(--summary-grid-span, 2);
      padding: 14px 10px;
      font-size: 14px;
    }

    &__cta {
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 20px;
      margin-top: 24px;
      padding: 10px 12px;
    }

    &__cta-text {
      font-size: 16px;
    }
  }

  @include tablet-landscape {
    padding: 56px 0 60px;

    &__title {
      font-size: 29px;
    }

    &__workflow {
      margin-top: 34px;
    }

    &__step {
      min-height: 174px;
      padding: 0 7px;
    }

    &__step-title {
      min-height: 45px;
      font-size: 14px;
    }

    &__icon-ring {
      width: 45px;
      height: 45px;
    }

    &__icon {
      width: 45px;
      height: 45px;
    }

    &__milestone {
      min-width: 98px;
      min-height: 34px;
      font-size: 16px;
    }

    &__summary {
      gap: 16px;
      margin-top: 22px;
    }

    &__summary-card {
      padding: 14px 10px;
      font-size: 16px;
    }

    &__cta {
      margin-top: 24px;
      padding: 10px 12px;
    }

    &__cta-text {
      font-size: 16px;
    }

    &__cta-link {
      min-height: 42px;
      padding: 10px 18px;
      font-size: 16px;
    }
    a:visited {
      color: #fff;
    }
  }
}
</style>
